import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ProjectStatusTile from '../components/tiles/ProjectStatusTile';

// Headline counts are derived at runtime from the published Developer Portal
// document (workflows/APIs/operations are env-invariant, so the TEST copy is a
// fine source), so the home page stays accurate on every publish instead of
// drifting. The fallbacks are the values known-good at time of writing.
const FALLBACK = { workflows: 34, apis: 8, operations: 36 };
const STATS_URL = 'platform-content/synergy-application/test/developer-portal.json';

interface Stats {
  workflows: number;
  apis: number;
  operations: number;
}

export default function Home() {
  const [stats, setStats] = useState<Stats>(FALLBACK);

  useEffect(() => {
    let cancelled = false;
    fetch(STATS_URL, { cache: 'no-cache' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((doc) => {
        if (cancelled) return;
        const meta: Record<string, string> = Object.fromEntries(
          (doc.metadata ?? []).map((m: { label: string; value: string }) => [m.label, m.value]),
        );
        let workflows = 0;
        const walk = (b: unknown): void => {
          if (Array.isArray(b)) return b.forEach(walk);
          if (b && typeof b === 'object') {
            const node = b as Record<string, unknown>;
            if (node.type === 'cardGrid' && Array.isArray(node.cards)) workflows += node.cards.length;
            Object.values(node).forEach(walk);
          }
        };
        const section = (doc.sections ?? []).find((s: { id?: string }) => s.id === 'workflows');
        if (section) walk(section.blocks);
        setStats({
          workflows: workflows || FALLBACK.workflows,
          apis: Number(meta['APIs published']) || FALLBACK.apis,
          operations: Number(meta['Operations']) || FALLBACK.operations,
        });
      })
      .catch(() => {
        /* keep fallbacks */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="page home-page">
      <h2>What is the Synergy Platform?</h2>
      <div className="home-info-box">
        <p>
          The Synergy Platform is an{' '}
          <strong className="home-accent">Azure-native integration platform</strong> that
          orchestrates <strong>contact and case master data</strong> across CIPD's
          enterprise systems. A single API call synchronises records across{' '}
          <strong>Salesforce CRM</strong>, <strong>Semarchy MDM</strong>, and{' '}
          <strong>Auth0/Okta</strong> identity &mdash; with further integrations to{' '}
          <strong>NetSuite</strong> and <strong>Creatio</strong> &mdash; eliminating data
          silos, manual synchronisation, and identity gaps.
        </p>
        <p>
          Every write is a <strong>linear, idempotent operation replayed whole</strong> on
          failure (v3.0): the shared identity resolver stamps a stable contact number, each
          step runs unconditionally, and the error framework replays the entire operation so
          it self-heals with no bespoke repair code. The platform also provides{' '}
          <strong>automated Azure VM provisioning</strong> via a self-service API.
        </p>
        <div className="home-stats">
          <span className="home-stat">{stats.workflows} Workflows</span>
          <span className="home-stat">{stats.apis} APIs</span>
          <span className="home-stat">{stats.operations} Operations</span>
          <span className="home-stat">5 Integration Targets</span>
          <span className="home-stat">4 Environments</span>
          <span className="home-stat">100% Private Networking</span>
        </div>
      </div>

      <h3 className="home-section-title">Live Project Status</h3>
      <div className="tile-grid overview-grid">
        <ProjectStatusTile />
      </div>

      <h3 className="home-section-title">How It Works</h3>
      <div className="home-info-box">
        <p>
          Integration partners and internal systems call the Synergy APIs through{' '}
          <Link to="/developer-portal">Azure API Management</Link>. APIM validates the
          request (rejecting bad payloads with HTTP 400 before they reach a workflow),
          applies rate limiting and CORS, then routes writes to <strong>Azure Service
          Bus</strong> for asynchronous processing. Service Bus decouples the gateway from
          the backend, providing resilience, retry, and dead-lettering.
        </p>
        <p>
          <Link to="/application">Logic App workflows</Link> consume Service Bus messages
          and orchestrate the downstream calls &mdash; the resolver returns the canonical
          contact number, Salesforce is upserted, then the secondaries (Semarchy, Auth0,
          Case) run, every step idempotent. Token acquisition is handled by shared utility
          workflows with 55-minute APIM caching.
        </p>
        <p>
          Two invocation patterns are used:{' '}
          <strong>Fire-and-Forget via Service Bus</strong> (APIM returns 202 Accepted while
          the workflow runs asynchronously) for create, update, delete, merge, case, and
          server operations; and <strong>Direct HTTP</strong> (APIM calls the Logic App
          synchronously over the internal VNet) for the find operations, which return
          results in the same round-trip.
        </p>
      </div>

      <h3 className="home-section-title">Platform Components</h3>
      <div className="home-card-grid">
        <article className="home-card">
          <header className="home-card-header home-card-header--purple">
            <h3>API Management (APIM)</h3>
          </header>
          <div className="home-card-body">
            <p>
              The API gateway for all integration traffic. Internal VNet mode with custom
              domains, subscription-key authentication, per-operation request validation,
              rate limiting, and CORS. Caches OAuth tokens for Salesforce, Semarchy, Auth0
              and NetSuite with a 55-minute TTL.
            </p>
            <div className="home-tags">
              <span className="home-tag home-tag--purple">Gateway</span>
              <span className="home-tag home-tag--purple">Validation</span>
              <span className="home-tag home-tag--purple">Rate Limiting</span>
              <span className="home-tag home-tag--purple">Token Cache</span>
            </div>
            <Link to="/developer-portal" className="home-card-link home-card-link--purple">
              Developer Portal &rarr;
            </Link>
          </div>
        </article>

        <article className="home-card">
          <header className="home-card-header home-card-header--yellow">
            <h3>Logic Apps Workflows</h3>
          </header>
          <div className="home-card-body">
            <p>
              {stats.workflows} workflows: contact operations (create, update, find, delete,
              merge, form, bulk, email-change), case operations (create, find, update,
              delete), the error-management framework (capture, categorise, scheduled retry,
              dead-letter, resubmit), shared utilities (identity resolver, token acquisition,
              normalise, validate, email, health), member lookup, server provisioning, and
              the Auth0 registration link.
            </p>
            <div className="home-tags">
              <span className="home-tag home-tag--yellow">{stats.workflows} Workflows</span>
              <span className="home-tag home-tag--yellow">Idempotent Replay (v3)</span>
              <span className="home-tag home-tag--yellow">VNet Integrated</span>
            </div>
            <Link to="/application" className="home-card-link home-card-link--yellow">
              Application Guide &rarr;
            </Link>
          </div>
        </article>

        <article className="home-card">
          <header className="home-card-header home-card-header--purple">
            <h3>Azure Service Bus</h3>
          </header>
          <div className="home-card-body">
            <p>
              Premium tier message broker. A request topic per write workflow decouples APIM
              from the asynchronous Logic Apps, and a central retry queue drives broker-side
              scheduled retries and dead-lettering for the error framework (F6). RBAC-only
              authentication (no SAS keys).
            </p>
            <div className="home-tags">
              <span className="home-tag home-tag--purple">Premium</span>
              <span className="home-tag home-tag--purple">Scheduled Retry</span>
              <span className="home-tag home-tag--purple">RBAC</span>
              <span className="home-tag home-tag--purple">Dead-Letter</span>
            </div>
            <Link to="/application" className="home-card-link home-card-link--purple">
              Service Bus Topics &rarr;
            </Link>
          </div>
        </article>

        <article className="home-card">
          <header className="home-card-header home-card-header--yellow">
            <h3>Infrastructure (IaC)</h3>
          </header>
          <div className="home-card-body">
            <p>
              Fully automated Bicep deployment with a private VNet, private endpoints,
              private DNS zones, NSG rules, Key Vault, Application Insights, and diagnostic
              settings streaming to Log Analytics. Zero manual post-deployment steps.
            </p>
            <div className="home-tags">
              <span className="home-tag home-tag--yellow">Bicep</span>
              <span className="home-tag home-tag--yellow">Private VNet</span>
              <span className="home-tag home-tag--yellow">Key Vault</span>
              <span className="home-tag home-tag--yellow">Monitoring</span>
            </div>
            <Link to="/infrastructure" className="home-card-link home-card-link--yellow">
              Infrastructure Guide &rarr;
            </Link>
          </div>
        </article>
      </div>

      <h3 className="home-section-title">Error Management (v3.0)</h3>
      <div className="home-info-box">
        <p>
          Every operation is idempotent and replayable, so a partial failure is healed by
          replaying the whole operation rather than by bespoke repair code. Failures are
          captured to a durable store, categorised by HTTP-status class, and&mdash;for
          transient technical failures&mdash;retried automatically via{' '}
          <strong>Service Bus scheduled messages</strong> with exponential backoff; poison
          messages dead-letter and are escalated for review. Operators can resubmit any
          captured failure from the dashboard. Notifications fire once per failure, and a
          reconciliation pass auto-resolves anything that heals on replay.
        </p>
      </div>

      <h3 className="home-section-title">External System Integrations</h3>
      <div className="home-integration-grid">
        <div className="home-integration-card">
          <h4>Salesforce CRM</h4>
          <p>
            Contact and case lifecycle &mdash; create, update, find, delete, and merge via
            REST and SOQL, with an idempotent upsert on the contact number. OAuth 2.0 with
            APIM token caching.
          </p>
        </div>
        <div className="home-integration-card">
          <h4>Semarchy xDM</h4>
          <p>
            Master Data Management authority &mdash; the golden record source for contact
            data. Handles record persistence, address linkage keyed on the contact number,
            and duplicate resolution via REST.
          </p>
        </div>
        <div className="home-integration-card">
          <h4>Auth0 / Okta</h4>
          <p>
            Identity provider for user provisioning and management. New contacts receive
            user identities; deletions cascade to revoke access. Auth0 fronts Okta via a
            Custom DB passthrough connection.
          </p>
        </div>
        <div className="home-integration-card">
          <h4>NetSuite</h4>
          <p>
            SuiteTalk REST integration via a machine-to-machine bearer token, with
            APIM-cached token acquisition.
          </p>
        </div>
        <div className="home-integration-card">
          <h4>Creatio</h4>
          <p>
            Learning/booking integration &mdash; bookings drive contact creation and the
            allocated contact number is written back.
          </p>
        </div>
      </div>

      <h3 className="home-section-title">Request Flow</h3>
      <div className="home-info-box">
        <div className="home-flow">
          <span className="home-flow-step home-flow-step--purple">Client</span>
          <span className="home-flow-arrow">&rarr;</span>
          <span className="home-flow-step home-flow-step--purple">APIM</span>
          <span className="home-flow-arrow">&rarr;</span>
          <span className="home-flow-step home-flow-step--yellow">Service Bus</span>
          <span className="home-flow-arrow">&rarr;</span>
          <span className="home-flow-step home-flow-step--yellow">Logic App</span>
          <span className="home-flow-arrow">&rarr;</span>
          <span className="home-flow-step home-flow-step--purple-light">
            Salesforce + Semarchy + Auth0
          </span>
        </div>
        <ol className="home-flow-steps">
          <li>
            Client sends a request to <strong>APIM</strong> with a subscription key and
            correlation ID
          </li>
          <li>
            APIM validates the payload, applies rate limits, and publishes to a{' '}
            <strong>Service Bus topic</strong> (or calls the workflow directly for finds)
          </li>
          <li>
            The target <strong>Logic App workflow</strong> consumes the message and resolves
            the canonical contact number
          </li>
          <li>
            Salesforce is upserted, then the secondaries (Semarchy, Auth0, Case) run &mdash;
            every step idempotent
          </li>
          <li>
            On success: the correlation ID is returned. On failure: the operation is
            captured and <strong>replayed</strong> automatically (Service Bus scheduled
            retry), with an email notification and operator resubmit available
          </li>
        </ol>
      </div>

      <h3 className="home-section-title">Documentation</h3>
      <div className="home-card-grid home-card-grid--three">
        <article className="home-card">
          <header className="home-card-header home-card-header--purple">
            <h3>Infrastructure Guide</h3>
          </header>
          <div className="home-card-body">
            <p>
              Bicep IaC, VNet architecture, private endpoints, networking, security
              hardening, pipelines, and certificates.
            </p>
            <Link to="/infrastructure" className="home-card-link home-card-link--purple">
              Open guide &rarr;
            </Link>
          </div>
        </article>

        <article className="home-card">
          <header className="home-card-header home-card-header--yellow">
            <h3>Application Guide</h3>
          </header>
          <div className="home-card-body">
            <p>
              Logic App workflows, Service Bus integration, APIM policies, deployment
              pipeline, and messaging patterns.
            </p>
            <Link to="/application" className="home-card-link home-card-link--yellow">
              Open guide &rarr;
            </Link>
          </div>
        </article>

        <article className="home-card">
          <header className="home-card-header home-card-header--purple">
            <h3>Developer Portal</h3>
          </header>
          <div className="home-card-body">
            <p>
              API catalogue, platform workflows, OpenAPI specs, request body examples, field
              references, access management, and getting started.
            </p>
            <Link to="/developer-portal" className="home-card-link home-card-link--purple">
              Open portal &rarr;
            </Link>
          </div>
        </article>

        <article className="home-card">
          <header className="home-card-header home-card-header--yellow">
            <h3>Estate Operations Manual</h3>
          </header>
          <div className="home-card-body">
            <p>
              The master manual for how the entire estate is built and run: architecture,
              environments, networking, identity, data stores, pipelines, integrations,
              monitoring, and step-by-step operational runbooks.
            </p>
            <Link
              to="/azure/operations/estate-operations-manual"
              className="home-card-link home-card-link--yellow"
            >
              Open manual &rarr;
            </Link>
          </div>
        </article>
      </div>
    </div>
  );
}
